import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface HeatmapPoint {
  lat: number;
  lng: number;
  weight: number;
}

interface ClusteredPoint {
  lat: number;
  lng: number;
  count: number;
  reports: any[];
}

interface ServiceArea {
  id: string;
  name: string;
  coordinates: [number, number][];
  color: string;
  opacity: number;
}

interface GISMapProps {
  heatmapData: HeatmapPoint[];
  clusteredData: ClusteredPoint[];
  serviceAreas: ServiceArea[];
  technicians: any[];
  showHeatmap: boolean;
  showClusters: boolean;
  showServiceAreas: boolean;
  showTechnicians: boolean;
  onPointClick?: (data: any) => void;
}

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const GISMap: React.FC<GISMapProps> = ({
  heatmapData,
  clusteredData,
  serviceAreas,
  technicians,
  showHeatmap,
  showClusters,
  showServiceAreas,
  showTechnicians,
  onPointClick
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default center (Howick, KZN - Umngeni Municipality)
  const MAP_DEFAULT_CENTER: [number, number] = [-29.4858, 30.2306];
  const MAP_DEFAULT_ZOOM = 12;

  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up existing map if it exists
    if (mapInstanceRef.current) {
      console.log('Cleaning up existing GIS map...');
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    try {
      console.log('Initializing GIS Map...');
      
      // Create the map instance
      const map = L.map(mapRef.current, {
        center: MAP_DEFAULT_CENTER,
        zoom: MAP_DEFAULT_ZOOM,
        zoomControl: true,
        attributionControl: true
      });

      console.log('GIS Map instance created, adding tile layers...');

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map);

      // Add additional tile layers
      const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '© <a href="https://www.esri.com/">Esri</a>',
        maxZoom: 19
      });

      const streetLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      });

      // Layer control
      const baseMaps = {
        "Street": streetLayer,
        "Satellite": satelliteLayer
      };

      L.control.layers(baseMaps).addTo(map);

      console.log('GIS Map tile layers added successfully');

      // Store map instance
      mapInstanceRef.current = map;
      setIsLoading(false);

      console.log('GIS Map initialized successfully');
    } catch (err) {
      console.error('Failed to initialize GIS Map:', err);
      setError(`Failed to load GIS map: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        console.log('Cleaning up GIS map on unmount...');
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current) {
      updateMapLayers();
    }
  }, [heatmapData, clusteredData, serviceAreas, technicians, showHeatmap, showClusters, showServiceAreas, showTechnicians]);



  const updateMapLayers = () => {
    if (!mapInstanceRef.current) return;

    // Clear existing layers
    mapInstanceRef.current.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) return; // Keep base tiles
      mapInstanceRef.current!.removeLayer(layer);
    });

    // Add heatmap layer
    if (showHeatmap && heatmapData.length > 0) {
      const heatmapLayer = L.layerGroup();
      
      heatmapData.forEach(point => {
        const radius = Math.max(10, Math.min(50, point.weight * 5));
        const intensity = Math.min(1, point.weight / 10);
        
        const circle = L.circle([point.lat, point.lng], {
          radius: radius,
          fillColor: `rgba(255, 0, 0, ${intensity})`,
          color: 'red',
          weight: 1,
          opacity: intensity,
          fillOpacity: intensity * 0.6
        });
        
        if (mapInstanceRef.current) {
          circle.addTo(heatmapLayer);
        }
      });
      
      heatmapLayer.addTo(mapInstanceRef.current);
    }

    // Add clustered points layer
    if (showClusters && clusteredData.length > 0) {
      const clusterLayer = L.layerGroup();
      
      clusteredData.forEach(cluster => {
        const markerSize = Math.max(20, Math.min(40, cluster.count * 5));
        const marker = L.circleMarker([cluster.lat, cluster.lng], {
          radius: markerSize,
          fillColor: '#3b82f6',
          color: '#1e40af',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.7
        });
        
        // Add popup with cluster info
        const popupContent = `
          <div style="min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 16px; font-weight: 600;">
              Cluster (${cluster.count} reports)
            </h3>
            <div style="font-size: 14px; color: #6b7280;">
              <strong>Location:</strong> ${cluster.lat.toFixed(4)}, ${cluster.lng.toFixed(4)}
            </div>
            ${cluster.reports.length > 0 ? `
              <div style="margin-top: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">Sample Reports:</div>
                ${cluster.reports.slice(0, 3).map((report: any) => `
                  <div style="padding: 4px; background: #f9fafb; border-radius: 4px; margin-bottom: 4px; font-size: 12px;">
                    ${report.title || 'Untitled Report'}
                  </div>
                `).join('')}
                ${cluster.reports.length > 3 ? `<div style="font-size: 11px; color: #9ca3af;">...and ${cluster.reports.length - 3} more</div>` : ''}
              </div>
            ` : ''}
          </div>
        `;
        
        if (mapInstanceRef.current) {
          marker.bindPopup(popupContent);
          marker.addTo(clusterLayer);
          
          if (onPointClick) {
            marker.on('click', () => onPointClick(cluster));
          }
        }
      });
      
      clusterLayer.addTo(mapInstanceRef.current);
    }

    // Add service areas layer
    if (showServiceAreas && serviceAreas.length > 0) {
      const serviceAreaLayer = L.layerGroup();
      
      serviceAreas.forEach(area => {
        if (area.coordinates.length > 2) {
          const polygon = L.polygon(area.coordinates, {
            color: area.color,
            weight: 2,
            opacity: 0.8,
            fillColor: area.color,
            fillOpacity: area.opacity
          });
          
          const popupContent = `
            <div style="min-width: 200px;">
              <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 16px; font-weight: 600;">
                ${area.name}
              </h3>
              <div style="font-size: 14px; color: #6b7280;">
                Service Area Coverage
              </div>
            </div>
          `;
          
                  if (mapInstanceRef.current) {
          polygon.bindPopup(popupContent);
          polygon.addTo(serviceAreaLayer);
        }
        }
      });
      
      serviceAreaLayer.addTo(mapInstanceRef.current);
    }

    // Add technicians layer
    if (showTechnicians && technicians.length > 0) {
      const technicianLayer = L.layerGroup();
      
      technicians.forEach(tech => {
        if (tech.latitude && tech.longitude) {
          const colors = {
            available: '#10b981',
            busy: '#f59e0b',
            online: '#3b82f6',
            offline: '#6b7280'
          };

          const color = colors[tech.status as keyof typeof colors] || '#6b7280';
          
          const marker = L.circleMarker([tech.latitude, tech.longitude], {
            radius: 8,
            fillColor: color,
            color: 'white',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8
          });
          
          const popupContent = `
            <div style="min-width: 200px;">
              <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 16px; font-weight: 600;">
                ${tech.firstName || 'Unknown'} ${tech.lastName || 'User'}
              </h3>
              <div style="margin-bottom: 4px;">
                <span style="
                  display: inline-block;
                  padding: 4px 8px;
                  background: ${color}20;
                  color: ${color};
                  border-radius: 6px;
                  font-size: 12px;
                  font-weight: 500;
                  text-transform: capitalize;
                ">
                  ${tech.status || 'unknown'}
                </span>
              </div>
              <div style="font-size: 14px; color: #6b7280;">
                <strong>Department:</strong> ${tech.departmentName || 'Unknown'}
              </div>
            </div>
          `;
          
          if (mapInstanceRef.current) {
            marker.bindPopup(popupContent);
            marker.addTo(technicianLayer);
          }
        }
      });
      
      technicianLayer.addTo(mapInstanceRef.current);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Map Error</div>
          <div className="text-red-500 text-sm">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <div className="text-gray-600 dark:text-gray-400">Loading GIS map...</div>
          </div>
        </div>
      )}
      <div 
        ref={mapRef} 
        className="w-full h-96 rounded-lg border border-gray-200"
        style={{ minHeight: '500px' }}
      />
    </div>
  );
};

export default GISMap;
